// ---------------------------------------------------------------------------
// Interactive Elements — Doors and Ladders
// Client-side rendering and state management for interactive map elements.
// Server-authoritative state is received via snapshots; this module handles
// visual representation, animations, and player proximity detection.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import bundledInteractiveConfigs from '@shared/interactive-map-config.json';

import type { GraphicalMapData, GraphicalMapMarker } from './map-asset-format';
import { computeDoorAngleDeg, DoorVisualState } from '../gameplay/door';
import { setInteractionPrompt } from '../ui/interaction-prompt';
import { keybindManager, KeybindManager } from '../core/keybinds';

// ── Constants ──

/** Time in seconds for a door to fully open/close. */
const DOOR_OPEN_SPEED = 2.0;
/** Max distance (meters) from a door for the player to interact. */
const DOOR_INTERACT_RANGE = 2.5;
/** Door mesh dimensions (meters). */
const DOOR_WIDTH = 1.2;
const DOOR_HEIGHT = 2.4;
const DOOR_THICKNESS = 0.2;
/** Default door slab tint — overridden for gates so they read as steel. */
const DOOR_COLOR = 0x8b6f47;
const GATE_COLOR = 0x4a4f55;

/** Ladder highlight proximity range (meters). */
const LADDER_HIGHLIGHT_RANGE = 2.0;

// ── Door State Enum ──

export enum DoorState {
  Closed = 0,
  Opening = 1,
  Open = 2,
  Closing = 3,
}

// ── Config Types ──

export interface DoorConfig {
  id: string;
  position: [number, number, number];
  rotation: [number, number, number];
  swingAngleDeg: number;
  /** Optional slab width (m). Defaults to DOOR_WIDTH when omitted. */
  width?: number;
  /** Optional slab height (m). Defaults to DOOR_HEIGHT when omitted. */
  height?: number;
  /** Optional override for the proximity prompt range (m). */
  interactRange?: number;
  /** Optional prompt noun ("DOOR" by default, "GATE" for compound gates). */
  label?: string;
}

export interface LadderConfig {
  id: string;
  position: [number, number, number];
  rotation: [number, number, number];
  bottom: number;
  top: number;
}

export interface ReplicatedDoorState {
  id: string;
  state: number;
  stateStart: number;
  animTime: number;
}

function toDoorState(state: number): DoorState {
  switch (state) {
    case DoorState.Opening:
      return DoorState.Opening;
    case DoorState.Open:
      return DoorState.Open;
    case DoorState.Closing:
      return DoorState.Closing;
    case DoorState.Closed:
    default:
      return DoorState.Closed;
  }
}

// ── Door Renderer ──

export class DoorRenderer {
  readonly id: string;
  readonly mesh: THREE.Group;
  state: DoorState = DoorState.Closed;
  currentAngle = 0;
  /** Per-door interaction range (m); falls back to the global default. */
  readonly interactRange: number;
  /** Per-door prompt noun ("DOOR" / "GATE"). */
  readonly label: string;
  /** Slab width used for visuals and proximity checks. */
  readonly width: number;
  /** Slab height used for visuals and proximity checks. */
  readonly height: number;
  private readonly swingAngleDeg: number;
  private readonly pivotGroup: THREE.Group;

  constructor(config: DoorConfig) {
    this.id = config.id;
    this.swingAngleDeg = config.swingAngleDeg;
    this.interactRange = config.interactRange ?? DOOR_INTERACT_RANGE;
    this.label = config.label ?? 'DOOR';

    const width = config.width ?? DOOR_WIDTH;
    const height = config.height ?? DOOR_HEIGHT;
    this.width = width;
    this.height = height;
    // Gates render in steel grey so they're visually distinct from interior
    // wooden doors. Plain doors keep the warm wood tint.
    const isGate = this.label === 'GATE';

    // Create door mesh hierarchy
    this.mesh = new THREE.Group();
    this.mesh.userData.cp_collision = false;
    this.mesh.userData.cp_interactive = true;
    this.mesh.userData.cp_interactive_type = 'door';
    this.mesh.position.set(...config.position);
    this.mesh.rotation.set(...config.rotation);

    // Pivot group for rotation animation (pivots around edge, not center)
    this.pivotGroup = new THREE.Group();
    this.pivotGroup.userData.cp_collision = false;
    this.pivotGroup.position.set(-width / 2, 0, 0);
    this.mesh.add(this.pivotGroup);

    // Door slab
    const geometry = new THREE.BoxGeometry(width, height, DOOR_THICKNESS);
    const material = new THREE.MeshStandardMaterial({
      color: isGate ? GATE_COLOR : DOOR_COLOR,
      roughness: isGate ? 0.55 : 0.8,
      metalness: isGate ? 0.6 : 0.1,
    });
    const doorMesh = new THREE.Mesh(geometry, material);
    doorMesh.userData.cp_collision = false;
    // Offset so pivot is at the edge
    doorMesh.position.set(width / 2, height / 2, 0);
    this.pivotGroup.add(doorMesh);
  }

  /** Distance from a player to the nearest point on the closed door opening. */
  getInteractionDistance(pos: { x: number; y: number; z: number }): number {
    const dx = pos.x - this.mesh.position.x;
    const dy = pos.y - this.mesh.position.y;
    const dz = pos.z - this.mesh.position.z;
    const yaw = this.mesh.rotation.y;
    const cosYaw = Math.cos(yaw);
    const sinYaw = Math.sin(yaw);
    const localX = dx * cosYaw + dz * sinYaw;
    const localZ = -dx * sinYaw + dz * cosYaw;

    const lateralExcess = Math.max(Math.abs(localX) - this.width / 2, 0);
    const depth = Math.abs(localZ);
    const verticalExcess =
      dy < 0 ? -dy :
      dy > this.height ? dy - this.height :
      0;

    return Math.sqrt(
      lateralExcess * lateralExcess +
      depth * depth +
      verticalExcess * verticalExcess,
    );
  }

  /** Toggle the door open/close state. */
  toggle(): void {
    switch (this.state) {
      case DoorState.Closed:
        this.state = DoorState.Opening;
        break;
      case DoorState.Open:
        this.state = DoorState.Closing;
        break;
      case DoorState.Opening:
        this.state = DoorState.Closing;
        break;
      case DoorState.Closing:
        this.state = DoorState.Opening;
        break;
    }
  }

  /** Update door animation. Call each frame with delta time. */
  update(dt: number): void {
    const speed = this.swingAngleDeg / DOOR_OPEN_SPEED; // degrees per second

    switch (this.state) {
      case DoorState.Opening:
        this.currentAngle += speed * dt;
        if (this.currentAngle >= this.swingAngleDeg) {
          this.currentAngle = this.swingAngleDeg;
          this.state = DoorState.Open;
        }
        break;
      case DoorState.Closing:
        this.currentAngle -= speed * dt;
        if (this.currentAngle <= 0) {
          this.currentAngle = 0;
          this.state = DoorState.Closed;
        }
        break;
    }

    // Apply rotation to pivot
    this.pivotGroup.rotation.set(0, (this.currentAngle * Math.PI) / 180, 0);
  }

  /** Set door to a specific server-authoritative state. */
  setServerState(state: DoorState, angle: number): void {
    this.state = state;
    this.currentAngle = angle;
    this.pivotGroup.rotation.set(0, (angle * Math.PI) / 180, 0);
  }

  /** Apply a server-replicated state-machine snapshot to the visual door. */
  applyServerSnapshot(snapshot: ReplicatedDoorState, nowSeconds: number): void {
    const state = toDoorState(snapshot.state);
    const angle = computeDoorAngleDeg(
      {
        state: state as unknown as DoorVisualState,
        stateStart: snapshot.stateStart,
        animTime: snapshot.animTime,
      },
      nowSeconds,
      this.swingAngleDeg,
    );
    this.setServerState(state, angle);
  }

  /** Dispose of Three.js resources. */
  dispose(): void {
    this.pivotGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        if (obj.material instanceof THREE.Material) {
          obj.material.dispose();
        }
      }
    });
  }
}

// ── Ladder Renderer ──

export class LadderRenderer {
  readonly id: string;
  readonly mesh: THREE.Group;
  readonly bottom: number;
  readonly top: number;
  isHighlighted = false;
  private material: THREE.MeshStandardMaterial;

  constructor(config: LadderConfig) {
    this.id = config.id;
    this.bottom = config.bottom;
    this.top = config.top;

    const height = this.top - this.bottom;

    this.mesh = new THREE.Group();
    this.mesh.position.set(config.position[0], config.position[1], config.position[2]);
    this.mesh.rotation.set(...config.rotation);

    // Ladder frame (two vertical rails + rungs)
    this.material = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.6,
      metalness: 0.4,
    });

    const railWidth = 0.05;
    const railDepth = 0.05;
    const ladderWidth = 0.6;

    // Left rail
    const leftRailGeo = new THREE.BoxGeometry(railWidth, height, railDepth);
    const leftRail = new THREE.Mesh(leftRailGeo, this.material);
    leftRail.position.set(-ladderWidth / 2, height / 2, 0);
    this.mesh.add(leftRail);

    // Right rail
    const rightRailGeo = new THREE.BoxGeometry(railWidth, height, railDepth);
    const rightRail = new THREE.Mesh(rightRailGeo, this.material);
    rightRail.position.set(ladderWidth / 2, height / 2, 0);
    this.mesh.add(rightRail);

    // Rungs (every 0.4m)
    const rungSpacing = 0.4;
    const rungCount = Math.floor(height / rungSpacing);
    const rungGeo = new THREE.CylinderGeometry(0.02, 0.02, ladderWidth);
    for (let i = 0; i < rungCount; i++) {
      const rung = new THREE.Mesh(rungGeo, this.material);
      rung.position.set(0, (i + 0.5) * rungSpacing, 0);
      rung.rotation.set(0, 0, Math.PI / 2);
      this.mesh.add(rung);
    }
  }

  /** Check if a player position is near this ladder (for highlight). */
  isPlayerNear(playerPos: { x: number; y: number; z: number }): boolean {
    const dx = playerPos.x - this.mesh.position.x;
    const dz = playerPos.z - this.mesh.position.z;
    const horizDist = Math.sqrt(dx * dx + dz * dz);
    return horizDist <= LADDER_HIGHLIGHT_RANGE;
  }

  /** Set highlight state (glow when player is near). */
  setHighlight(highlighted: boolean): void {
    this.isHighlighted = highlighted;
    if (highlighted) {
      this.material.emissive.set(0x444400);
      this.material.emissiveIntensity = 0.3;
    } else {
      this.material.emissive.set(0x000000);
      this.material.emissiveIntensity = 0;
    }
  }

  /** Dispose of Three.js resources. */
  dispose(): void {
    // All rail/rung meshes share a single material (`this.material`).
    // Traversing and calling dispose per-mesh would call dispose() once per
    // child — fine because three.js tolerates it, but wasteful. Dispose the
    // geometries and then the shared material exactly once.
    this.mesh.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
      }
    });
    this.material.dispose();
  }
}

// ── Interactive Element Manager ──

export class InteractiveElementManager {
  readonly doors = new Map<string, DoorRenderer>();
  readonly ladders = new Map<string, LadderRenderer>();
  private scene: THREE.Scene | null = null;

  /** Set the scene reference so dispose() can remove meshes. */
  setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  /** Add a door from map marker data. */
  addDoor(config: DoorConfig): DoorRenderer {
    const door = new DoorRenderer(config);
    this.doors.set(config.id, door);
    return door;
  }

  /** Add a ladder from map marker data. */
  addLadder(config: LadderConfig): LadderRenderer {
    const ladder = new LadderRenderer(config);
    this.ladders.set(config.id, ladder);
    return ladder;
  }

  /** Update all interactive elements (animations, highlights). */
  update(
    dt: number,
    playerPos?: { x: number; y: number; z: number },
    overridePrompt?: string | null,
  ): void {
    for (const door of this.doors.values()) {
      door.update(dt);
    }

    // Update ladder highlights based on player proximity
    if (playerPos) {
      for (const ladder of this.ladders.values()) {
        ladder.setHighlight(ladder.isPlayerNear(playerPos));
      }
    }

    // G9 — drive the bottom-center prompt. Caller may pass a
    // higher-priority `overridePrompt` (weapon pickup / bomb plant /
    // defuse) to take precedence over door+ladder hints; otherwise we
    // surface the door/ladder we're standing next to, with door winning
    // because the same interact press toggles it (G8 prioritization).
    //
    // UI-W6 — surface the player's currently-bound 'interact' key so the
    // hint reads e.g. "[F] OPEN DOOR" after a rebind, not always "[E]".
    if (!playerPos) {
      setInteractionPrompt(null);
      return;
    }
    const interactKeyCode = keybindManager.getKeyForAction('interact') ?? 'KeyE';
    const interactKeyLabel = KeybindManager.formatKeyName(interactKeyCode);
    if (overridePrompt) {
      setInteractionPrompt(overridePrompt, interactKeyLabel);
      return;
    }
    const door = this.getNearestDoor(playerPos);
    if (door) {
      const open = door.state === DoorState.Open || door.state === DoorState.Opening;
      setInteractionPrompt(
        `${open ? 'CLOSE' : 'OPEN'} ${door.label}`,
        interactKeyLabel,
      );
      return;
    }
    let ladderInRange = false;
    for (const ladder of this.ladders.values()) {
      if (ladder.isPlayerNear(playerPos)) {
        ladderInRange = true;
        break;
      }
    }
    setInteractionPrompt(ladderInRange ? 'CLIMB LADDER' : null, interactKeyLabel);
  }

  /** Find the nearest door within interaction range of a position. */
  getNearestDoor(
    pos: { x: number; y: number; z: number },
  ): DoorRenderer | null {
    let nearest: DoorRenderer | null = null;
    // Track the best (range-normalised) score so a 4 m gate doesn't always
    // shadow a 2.5 m interior door that the player is closer to.
    let bestRatio = Number.POSITIVE_INFINITY;

    for (const door of this.doors.values()) {
      const dist = door.getInteractionDistance(pos);
      const ratio = dist / door.interactRange;
      if (dist <= door.interactRange && ratio < bestRatio) {
        bestRatio = ratio;
        nearest = door;
      }
    }

    return nearest;
  }

  /** Apply authoritative door state snapshots from the server. */
  applyDoorStateSnapshots(snapshots: ReplicatedDoorState[], nowSeconds: number): void {
    for (const snapshot of snapshots) {
      const door = this.doors.get(snapshot.id);
      if (!door) continue;
      door.applyServerSnapshot(snapshot, nowSeconds);
    }
  }

  /** Get all Three.js groups to add to scene. */
  getMeshes(): THREE.Group[] {
    const meshes: THREE.Group[] = [];
    for (const door of this.doors.values()) {
      meshes.push(door.mesh);
    }
    for (const ladder of this.ladders.values()) {
      meshes.push(ladder.mesh);
    }
    return meshes;
  }

  /** Dispose all resources. */
  dispose(): void {
    for (const door of this.doors.values()) {
      // BUG-25: Remove mesh from scene before disposing geometry/material
      if (this.scene) this.scene.remove(door.mesh);
      door.dispose();
    }
    for (const ladder of this.ladders.values()) {
      // BUG-25: Remove mesh from scene before disposing geometry/material
      if (this.scene) this.scene.remove(ladder.mesh);
      ladder.dispose();
    }
    this.doors.clear();
    this.ladders.clear();
    this.scene = null;
  }
}

// ── Per-Map Interactive Element Configs ──

export interface MapInteractiveConfig {
  doors: DoorConfig[];
  ladders: LadderConfig[];
}

type BundledInteractiveConfig = {
  doors?: Array<{
    id: string;
    position: number[];
    rotation: number[];
    swingAngleDeg: number;
    width?: number;
    height?: number;
    interactRange?: number;
    label?: string;
  }>;
  ladders?: Array<{
    id: string;
    position: number[];
    rotation: number[];
    bottom: number;
    top: number;
  }>;
};

const BUNDLED_INTERACTIVE_CONFIGS =
  bundledInteractiveConfigs as unknown as Record<string, BundledInteractiveConfig>;

// de_outpost_v6's GLB ships visual door geometry but no marker objects — its
// doors+ladders live in shared/interactive-map-config.json (the same source
// the server falls back to).

function toTuple3(values: number[]): [number, number, number] {
  return [
    values[0] ?? 0,
    values[1] ?? 0,
    values[2] ?? 0,
  ];
}

function cloneDoorConfig(config: DoorConfig): DoorConfig {
  return {
    id: config.id,
    position: [...config.position],
    rotation: [...config.rotation],
    swingAngleDeg: config.swingAngleDeg,
    ...(config.width !== undefined ? { width: config.width } : {}),
    ...(config.height !== undefined ? { height: config.height } : {}),
    ...(config.interactRange !== undefined ? { interactRange: config.interactRange } : {}),
    ...(config.label !== undefined ? { label: config.label } : {}),
  };
}

function cloneLadderConfig(config: LadderConfig): LadderConfig {
  return {
    id: config.id,
    position: [...config.position],
    rotation: [...config.rotation],
    bottom: config.bottom,
    top: config.top,
  };
}

function getBundledInteractiveConfigForMap(mapId: string): MapInteractiveConfig | null {
  const config = BUNDLED_INTERACTIVE_CONFIGS[mapId];
  if (!config) return null;
  return {
    doors: (config.doors ?? []).map((door) => cloneDoorConfig({
      id: door.id,
      position: toTuple3(door.position),
      rotation: toTuple3(door.rotation),
      swingAngleDeg: door.swingAngleDeg,
      ...(door.width !== undefined ? { width: door.width } : {}),
      ...(door.height !== undefined ? { height: door.height } : {}),
      ...(door.interactRange !== undefined ? { interactRange: door.interactRange } : {}),
      ...(door.label !== undefined ? { label: door.label } : {}),
    })),
    ladders: (config.ladders ?? []).map((ladder) => cloneLadderConfig({
      id: ladder.id,
      position: toTuple3(ladder.position),
      rotation: toTuple3(ladder.rotation),
      bottom: ladder.bottom,
      top: ladder.top,
    })),
  };
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function readString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
}

function markerToDoorConfig(marker: GraphicalMapMarker): DoorConfig {
  return {
    id: readString(marker.data.cp_id) ?? marker.name,
    position: [...marker.position],
    rotation: [...marker.rotation],
    swingAngleDeg: readNumber(marker.data.cp_swing_angle_deg) ?? 90,
  };
}

function markerToLadderConfig(marker: GraphicalMapMarker): LadderConfig {
  const bottom = readNumber(marker.data.cp_bottom) ?? marker.position[1];
  const top = readNumber(marker.data.cp_top) ?? marker.position[1] + 3;
  return {
    id: readString(marker.data.cp_id) ?? marker.name,
    position: [...marker.position],
    rotation: [...marker.rotation],
    bottom,
    top,
  };
}

/**
 * Pull door/ladder configs out of a GraphicalMapData. Returns null if the
 * map does not ship GLB-driven interactive markers (caller should then fall
 * back to the hardcoded per-map config).
 */
export function configFromGraphicalMap(
  data: GraphicalMapData | null | undefined,
): MapInteractiveConfig | null {
  if (!data) return null;
  const doors = data.doors ?? [];
  const ladders = data.ladders ?? [];
  if (doors.length === 0 && ladders.length === 0) return null;
  return {
    doors: doors.map(markerToDoorConfig),
    ladders: ladders.map(markerToLadderConfig),
  };
}

/**
 * Get interactive element configs for a map, preferring the bundled
 * shared/interactive-map-config.json entry. Falls back to GLB-driven markers
 * for maps without a bundled entry.
 */
export function getInteractiveConfigForMap(
  mapId: string,
  graphicalMap?: GraphicalMapData | null,
): MapInteractiveConfig {
  const bundled = getBundledInteractiveConfigForMap(mapId);
  if (bundled) return bundled;
  const fromGlb = configFromGraphicalMap(graphicalMap);
  if (fromGlb) return fromGlb;
  return { doors: [], ladders: [] };
}

/**
 * Hide and uncollide every static `*_leaf` mesh baked into the GLB by
 * `kit_v6.add_door_leaf`. Those leaves were authored as SYS-3 "always-ajar"
 * decoration on top of door FRAMES, but for any door that's also wired into
 * `interactive-map-config.json` the runtime DoorRenderer creates its own
 * swing-animated slab at the same world position. The two slabs overlap —
 * the static leaf is permanently at ~20° while the runtime slab is flush
 * (Closed=0°) so the doorway visually reads as a half-swung-open panel that
 * never moves. Worse, the static leaf is `collision=True` and ends up in
 * the BVH, so the player bumps it before reaching the door threshold.
 *
 * Returns the number of leaves suppressed (mostly useful for tests +
 * post-load logging).
 */
export function suppressStaticDoorLeaves(scene: THREE.Scene): number {
  let suppressed = 0;
  scene.traverse((obj) => {
    if (!obj.name || !/_leaf(_mesh)?$/.test(obj.name)) return;
    // Hide visually so the runtime DoorRenderer slab is the only thing the
    // player sees in the cutout.
    obj.visible = false;
    // Mark non-collidable so the next buildCollisionBVH pass skips it. The
    // wall + frame around the cutout still block movement; only the ajar
    // leaf collision goes away.
    obj.userData.cp_collision = false;
    suppressed += 1;
  });
  return suppressed;
}

/** Create and populate an InteractiveElementManager for the given map. */
export function createInteractiveElementsForMap(
  mapId: string,
  scene: THREE.Scene,
  graphicalMap?: GraphicalMapData | null,
): InteractiveElementManager {
  const manager = new InteractiveElementManager();
  manager.setScene(scene);
  const config = getInteractiveConfigForMap(mapId, graphicalMap);

  for (const doorCfg of config.doors) {
    const door = manager.addDoor(doorCfg);
    scene.add(door.mesh);
  }
  for (const ladderCfg of config.ladders) {
    const ladder = manager.addLadder(ladderCfg);
    scene.add(ladder.mesh);
  }

  // Suppress the GLB-baked SYS-3 leaves so they don't overlap with our
  // runtime slabs (see suppressStaticDoorLeaves for the full rationale).
  // Must run BEFORE buildCollisionBVH so the BVH excludes the leaf meshes.
  suppressStaticDoorLeaves(scene);

  return manager;
}
